import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { logger } from '@magnetar/utils'

// 定義回應介面
interface QueueMachineCallerResponse {
  success: boolean
  data?: {
    currentNumber: number
    currentName: string
    patientName?: string
  }
  error?: {
    code: string
    message: string
    details?: string
  }
}

// 定義錯誤代碼與對應的用戶友好信息
const ERROR_MESSAGES: Record<string, string> = {
  INVALID_QUEUE_MACHINE_ID: '無效的叫號機ID',
  QUEUE_MACHINE_NOT_FOUND: '找不到叫號機',
  INCREASE_QUEUE_NUMBER_FAILED: '增加號碼失敗',
  DISCOUNT_QUEUE_NUMBER_FAILED: '減少號碼失敗',
  SET_QUEUE_NUMBER_FAILED: '設定號碼失敗',
  INVALID_NUMBER: '無效的號碼',
  INTERNAL_SERVER_ERROR: '系統內部錯誤',
  QUEUE_MACHINE_NOT_ASSOCIATED: '叫號機未關聯診間',
  APPOINTMENT_SETTING_NOT_FOUND: '診間未設定預約系統',
  UNSUPPORTED_APPOINTMENT_TYPE: '不支援的預約類型',
  NO_APPOINTMENTS_TODAY: '今日沒有預約',
  NO_CHECKED_IN_APPOINTMENTS: '今日沒有已報到的預約',
  NO_PROCESSABLE_APPOINTMENTS: '找不到可處理的預約',
  APPOINTMENT_NOT_FOUND: '找不到指定流水號的預約',
  UNKNOWN_ERROR: '未知錯誤',
}

export const queueMachineCallerService = {
  // 增加叫號號碼
  increaseNumber: async (
    queueMachineId: number,
    increment: number = 1,
    token: string,
    patientName?: string,
  ): Promise<QueueMachineCallerResponse> => {
    try {
      const response = await apiClient.post(
        `/api/queue-machine/${queueMachineId}/caller/increase`,
        { increment, patientName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const responseData = error.response.data as QueueMachineCallerResponse

        // 增強錯誤信息
        if (responseData.error) {
          const errorCode = responseData.error.code
          const userFriendlyMessage = ERROR_MESSAGES[errorCode] || responseData.error.message

          return {
            success: false,
            error: {
              code: errorCode,
              message: userFriendlyMessage,
              details: responseData.error.message,
            },
          }
        }

        return responseData
      }

      // 處理網絡或其他非HTTP錯誤
      logger.error('增加叫號號碼錯誤:', error)
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: '增加叫號號碼時發生未知錯誤',
          details: error instanceof Error ? error.message : '連線失敗',
        },
      }
    }
  },

  // 減少叫號號碼
  discountNumber: async (
    queueMachineId: number,
    decrement: number = 1,
    token: string,
    patientName?: string,
  ): Promise<QueueMachineCallerResponse> => {
    try {
      const response = await apiClient.post(
        `/api/queue-machine/${queueMachineId}/caller/discount`,
        { decrement, patientName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const responseData = error.response.data as QueueMachineCallerResponse

        // 增強錯誤信息
        if (responseData.error) {
          const errorCode = responseData.error.code
          const userFriendlyMessage = ERROR_MESSAGES[errorCode] || responseData.error.message

          return {
            success: false,
            error: {
              code: errorCode,
              message: userFriendlyMessage,
              details: responseData.error.message,
            },
          }
        }

        return responseData
      }

      // 處理網絡或其他非HTTP錯誤
      logger.error('減少叫號號碼錯誤:', error)
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: '減少叫號號碼時發生未知錯誤',
          details: error instanceof Error ? error.message : '連線失敗',
        },
      }
    }
  },

  // 設定指定叫號號碼
  setNumber: async (
    queueMachineId: number,
    number: number,
    token: string,
    patientName?: string,
  ): Promise<QueueMachineCallerResponse> => {
    try {
      const response = await apiClient.post(
        `/api/queue-machine/${queueMachineId}/caller/set-number`,
        { number, patientName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const responseData = error.response.data as QueueMachineCallerResponse

        // 增強錯誤信息
        if (responseData.error) {
          const errorCode = responseData.error.code
          const userFriendlyMessage = ERROR_MESSAGES[errorCode] || responseData.error.message

          return {
            success: false,
            error: {
              code: errorCode,
              message: userFriendlyMessage,
              details: responseData.error.message,
            },
          }
        }

        return responseData
      }

      // 處理網絡或其他非HTTP錯誤
      logger.error('設定叫號號碼錯誤:', error)
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: '設定指定叫號號碼時發生未知錯誤',
          details: error instanceof Error ? error.message : '連線失敗',
        },
      }
    }
  },

  // 獲取錯誤代碼對應的用戶友好信息
  getErrorMessage: (errorCode: string): string => {
    return ERROR_MESSAGES[errorCode] || '操作失敗'
  },
}
